import React from 'react';
import styled from 'styled-components';
import { Dialog, DialogTrigger, Content, Heading, Flex, Text, Link, ActionButton } from '@adobe/react-spectrum';
import { VegaLite } from '@aep/react-vega';
import { useHistory } from 'react-router-dom';
import Bell from '@spectrum-icons/workflow/Bell';
import Close from '@spectrum-icons/workflow/Close';
import { getHealthIndexStatus, getSeverityCode, getBandDropAlert, getPlainTextSummary, getFixImpactLabel } from '../../widgetTab/components/healthIndexWidget.helpers';

const STATUS_COLORS = {
  Healthy: '#2D9D78',
  'At risk': '#E68619',
  Critical: '#C9252D',
};

const SEVERITY_COLORS = {
  S0: '#C9252D',
  S1: '#E68619',
  S2: '#2680EB',
  S3: '#6E6E6E',
};

const HeaderBar = styled(Flex)`
  background-color: #080808;
  color: white;
  padding: 20px 24px;
`;

const HeaderBadge = styled.span`
  background-color: ${props => props.color}33;
  color: ${props => props.color};
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  margin-left: 12px;
`;

const HeaderSubtext = styled.span`
  color: #B3B3B3;
  font-size: 13px;
`;

const SectionHeadingWrapper = styled.div`
  padding: 20px 24px 8px;
`;

const IssuesTableHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 100px 90px;
  max-width: 80%;
  gap: 16px;
  padding: 8px 24px;
  color: #4B4B4B;
  font-size: 14px;
  font-weight: 600;
`;

const IssueRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 100px 90px;
  max-width: 80%;
  gap: 16px;
  align-items: center;
  padding: 12px 24px;
  border-top: 1px solid #EAEAEA;
`;

const IssueTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const IssueDescription = styled.div`
  font-size: 12px;
  color: #6E6E6E;
`;

const NoSeverityBadge = styled.span`
  color: #909090;
  font-size: 12px;
`;

const SeverityBadge = styled.span`
  background-color: ${props => props.color}1A;
  color: ${props => props.color};
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
`;

const FixImpactBadge = styled.span`
  background-color: #E6F4EA;
  color: #1E7E34;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
`;

const EmptyState = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: #4B4B4B;
`;

const TrendSectionHeadingWrapper = styled(SectionHeadingWrapper)`
  border-top: 1px solid #EAEAEA;
`;

const AlertBanner = styled(Flex)`
  background-color: #FFF9E6;
  border-left: 4px solid #E68619;
  margin: 16px 24px 24px;
  padding: 16px;
  border-radius: 4px;
`;

const AlertTitle = styled.div`
  font-weight: 600;
  color: #323232;
  font-size: 14px;
`;

const AlertBody = styled.div`
  color: #6E6E6E;
  font-size: 13px;
  margin-top: 2px;
`;

const trendVegaConfig = (historicalHealthIndex, healthyThreshold, criticalThreshold) => ({
  $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
  description: 'Health Index 30-day trend.',
  height: 'container',
  width: 'container',
  data: { values: historicalHealthIndex },
  transform: [{ calculate: "format(datum.healthIndex, '.0f') + '%'", as: 'healthIndexLabel' }],
  mark: { type: 'line', interpolate: 'monotone', color: '#2D9D78', clip: true, point: true, tooltip: true },
  encoding: {
    x: {
      field: 'date',
      type: 'temporal',
      timeUnit: 'yearmonthdate',
      axis: null,
    },
    y: {
      field: 'healthIndex',
      type: 'quantitative',
      axis: { title: null, grid: true },
      scale: { domain: [0, 100], nice: true, zero: true },
    },
    tooltip: [
      { field: 'date', type: 'temporal', timeUnit: 'yearmonthdate', title: 'Date', format: '%b %d, %Y' },
      { field: 'healthIndexLabel', type: 'nominal', title: 'Health Index' },
    ],
  },
  config: {
    view: { stroke: 'transparent' },
    axis: { ticks: false, grid: true, labelColor: '#B3B3B3', tickColor: '#B3B3B3' },
  },
});

const HealthIndexBreakdownDialog = ({
  children,
  healthIndexScore,
  historicalHealthIndex,
  healthyThreshold,
  criticalThreshold,
  healthIndexBreakdown,
}) => {
  const history = useHistory();
  const status = getHealthIndexStatus(healthIndexScore, healthyThreshold, criticalThreshold);
  const issues = healthIndexBreakdown || [];
  const last30Days = (historicalHealthIndex || []).slice(-30);
  const bandDropAlert = getBandDropAlert(historicalHealthIndex || [], healthyThreshold, criticalThreshold);
  const topIssue = issues[0];

  return (
    <DialogTrigger type="fullscreen">
      {children}
      {close => (
        <Dialog>
          <Content UNSAFE_style={{ padding: 0 }}>
            <HeaderBar direction="row" alignItems="center" justifyContent="space-between">
              <Flex alignItems="center">
                <Heading level={3} margin="size-0">Health Index breakdown</Heading>
                <HeaderBadge color={STATUS_COLORS[status]}>{status} — {healthIndexScore}%</HeaderBadge>
              </Flex>
              <Flex alignItems="center" gap="size-200">
                <HeaderSubtext>Current detected issues</HeaderSubtext>
                <ActionButton isQuiet onPress={close} aria-label="Close" UNSAFE_style={{ color: 'white' }}>
                  <Close size="S"/>
                </ActionButton>
              </Flex>
            </HeaderBar>

            <SectionHeadingWrapper>
              <Heading level={3} margin="size-0">Issues pulling your score down</Heading>
            </SectionHeadingWrapper>

            {issues.length === 0 ? (
              <EmptyState>
                <Text>Nothing is currently pulling your score down. Nice work!</Text>
              </EmptyState>
            ) : (
              <>
                <IssuesTableHead>
                  <span>Issue</span>
                  <span>Sev.</span>
                  <span>Fix impact</span>
                  <span>Recommendation</span>
                </IssuesTableHead>
                {issues.map(issue => {
                  const severityCode = getSeverityCode(issue.severityLabel);
                  const severityColor = SEVERITY_COLORS[severityCode] || '#6E6E6E';
                  return (
                    <IssueRow key={issue.declarationId}>
                      <div>
                        <IssueTitle>{issue.title}</IssueTitle>
                        <IssueDescription>{getPlainTextSummary(issue.issueDescription, 100)}</IssueDescription>
                      </div>
                      {severityCode ? (
                        <SeverityBadge color={severityColor}>{severityCode}</SeverityBadge>
                      ) : (
                        <NoSeverityBadge>—</NoSeverityBadge>
                      )}
                      <FixImpactBadge>{getFixImpactLabel(issue, healthIndexScore)}</FixImpactBadge>
                      <Link onPress={() => {
                        close();
                        history.push('/commerce/tab/recommendations', { selectIssueCode: issue.issueCode });
                      }}>
                        <a target="_self" data-ui-id="health-index-breakdown-view-rec">View rec.</a>
                      </Link>
                    </IssueRow>
                  );
                })}
              </>
            )}

            <TrendSectionHeadingWrapper>
              <Heading level={3} margin="size-0">30-day health index trend</Heading>
            </TrendSectionHeadingWrapper>
            <div style={{ height: '200px', padding: '0 24px 16px' }}>
              <VegaLite
                width="container"
                height={180}
                spec={trendVegaConfig(last30Days, healthyThreshold, criticalThreshold)}
              />
            </div>

            {bandDropAlert && (
              <AlertBanner alignItems="start" gap="size-150">
                <Bell color="notice" size="S"/>
                <div>
                  <AlertTitle>
                    Score dropped into {bandDropAlert.band} on{' '}
                    {new Date(bandDropAlert.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </AlertTitle>
                  <AlertBody>
                    Fell from {bandDropAlert.fromScore}% to {bandDropAlert.toScore}%.
                    {topIssue && ` Fixing ${topIssue.title} could recover ${getFixImpactLabel(topIssue, healthIndexScore)}.`}
                  </AlertBody>
                </div>
              </AlertBanner>
            )}
          </Content>
        </Dialog>
      )}
    </DialogTrigger>
  );
};

export default HealthIndexBreakdownDialog;
